import { LocationSearch } from "./LocationSearch";
import { clsx } from "clsx";

/**
 * Props for the LocationSearchCTA component
 * @interface LocationSearchCTAProps
 * @property {string} [title="Find Trials Near You"] - The main heading text
 * @property {string} [subtitle="Enter your location to discover clinical trials in your area"] - The subheading text
 * @property {string} [buttonText="Search trials"] - Text to display on the search button
 * @property {(country: string, zipCode: string) => void} onSubmit - Callback function when location is submitted
 * @property {'default' | 'compact'} [variant='default'] - Visual style variant of the component
 * @property {boolean} [hideTitle=false] - Whether to hide the title and subtitle
 */
interface LocationSearchCTAProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  onSubmit: (country: string, zipCode: string) => void;
  variant?: "default" | "compact";
  hideTitle?: boolean;
}

/**
 * A call-to-action component featuring a location search functionality.
 * Displays a gradient background with decorative elements and a location search form.
 *
 * @component
 * @example
 * ```tsx
 * <LocationSearchCTA
 *   onSubmit={(country, zipCode) => {
 *     console.log(`Searching in ${country} with code ${zipCode}`);
 *   }}
 *   variant="compact"
 * />
 * ```
 */
export function LocationSearchCTA({
  title = "Find Trials Near You",
  subtitle = "Enter your location to discover clinical trials in your area",
  buttonText = "Search trials",
  onSubmit,
  variant = "default",
  hideTitle = false,
}: LocationSearchCTAProps) {
  return (
    <div className="relative bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl overflow-visible">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div
          className={clsx(
            "absolute -right-24 rounded-full bg-white/20",
            variant === "compact" ? "-top-16 w-48 h-48" : "-top-24 w-64 h-64",
          )}
        ></div>
        <div
          className={clsx(
            "absolute -left-24 rounded-full bg-white/20",
            variant === "compact"
              ? "-bottom-16 w-72 h-72"
              : "-bottom-24 w-96 h-96",
          )}
        ></div>
      </div>

      <div
        className={clsx(
          "relative px-8 text-center",
          variant === "compact" ? "py-8 sm:py-10" : "py-12 sm:px-12 sm:py-16",
        )}
      >
        {!hideTitle && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6">{title}</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {subtitle}
            </p>
          </>
        )}
        <div className="max-w-xl mx-auto">
          <LocationSearch onSubmit={onSubmit} buttonText={buttonText} />
        </div>
      </div>
    </div>
  );
}
